// src/lib/rangePresets.js — the quick-select buckets under the price and area filters.
//
// B839, owner 2026-08-19: *"We need to change quick select values according to deal type and
// property type too. To know which values are most logical one we should check database."* …
// *"these prices must not be hard coded rather function"* … *"realistic function that will filter
// out unrealistic values first then calculate"*.
//
// So: NOTHING IS WRITTEN DOWN. Every threshold on screen is computed, at render time, from the
// listings the panel is currently holding. No market number appears in this file, and none may
// ever be added — the moment a price is typed here it starts going stale, silently, in a country
// whose market moves faster than the code does.
//
// ── THE THREE STEPS, IN THIS ORDER ──────────────────────────────────────────────────────────
//   1. DISCARD what is not a real price (or a real area). See `realistic()`.
//   2. CUT AT THE TERCILES of what survives, so each chip carries about a third of the stock.
//   3. SNAP to a number a person would say out loud.
//
// Step 1 is the owner's correction and it is load-bearing, not hygiene: this corpus contains a
// house listed for sale at **$700** and another at **$355,555,555,555,555,540,000**. Both are
// real rows. Quantiles are robust to the top tail but the BOTTOM tail is what drags a low
// bucket down, and neither belongs in a statement about the market.
//
// ── WHY TERCILES AND NOT FRACTIONS OF THE AXIS ──────────────────────────────────────────────
// The first cut of this file put the cuts at 1/4, 1/2, 3/4 of the ceiling. That is a statement
// about the AXIS, not about the STOCK, and measured against the live corpus it failed exactly
// where it mattered most — rent · apartment, 16,559 listings and the commonest search in the
// system, put **62% of the inventory into one chip**, because the whole interquartile range
// ($499–$798) sat inside a single `$500–$1K` bucket. A chip that answers "most of them" answers
// nothing. Terciles are the fix by construction: three buckets, a third of the stock each,
// wherever the stock happens to be.
//   measured, cuts from the axis → cuts from the data:
//     rent · apartment   25/62/13  →  25/39/36
//     rent · house       51/38/11  →  30/32/38
//     sale · apartment   47/41/13  →  33/32/35
//     sale · house       44/40/16  →  32/34/35
//
// ⚠️ `niceRound` (1/2/5/10× magnitude) IS TOO COARSE FOR A DATA-DERIVED CUT and must not be used
// for one. Rent · apartment's upper tercile is 700; `niceRound(700)` is **1000**, which
// reproduces the exact bucket this change exists to remove. `niceCut` (two significant figures)
// keeps 700 at 700 and still reads as a price. `niceRound` survives only for the no-data
// fallback, where the input is already a round ceiling.

// How far from the middle a value may sit before it stops being a member of this market.
// A FACTOR, not a bound — it carries no units and no currency, so the same constant is right
// for USD, GEL and m², and stays right when every price in the country doubles.
// 20× either side is deliberately loose: it is meant to catch the typo and the placeholder, not
// to tidy the tails. A genuine luxury outlier is 5–10× the median and survives, as it should —
// it belongs in the top bucket, which is open-ended precisely so it can hold it.
const REALISTIC_SPAN = 20
// Below this, terciles describe the sample rather than the market, and the axis fallback is the
// more honest answer. The panel routinely holds hundreds of rows, so this only bites on a
// near-empty filter — which is exactly when a confident bucket would be a lie.
const MIN_SAMPLE = 20

/**
 * Drop what cannot be a real value for this segment, then return the survivors, sorted.
 * Median-anchored because the median is the one statistic the garbage cannot move: a single
 * 3.5e20 shifts a mean into orbit and leaves the median untouched.
 * ⚠️ SYMMETRIC, unlike `niceCeiling`'s trim, which guards the top tail only. The bottom is where
 * the damage is here — a $700 house sale is inside every axis and drags the first bucket down.
 * @returns {number[]} sorted survivors, or [] when there is not enough to judge.
 */
export function realistic(values) {
  const nums = (values || []).map(Number).filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b)
  if (nums.length < MIN_SAMPLE) return []
  const med = nums[Math.floor(nums.length / 2)]
  if (!(med > 0)) return []
  return nums.filter(v => v >= med / REALISTIC_SPAN && v <= med * REALISTIC_SPAN)
}

// Two significant figures — the resolution a data-derived cut needs. 132,000 → 130,000 and
// 700 stays 700, where a one-figure snap would say 100,000 and 1,000.
export function niceCut(n) {
  if (!(n > 0) || !Number.isFinite(n)) return n
  const step = 10 ** Math.floor(Math.log10(n)) / 10
  return Math.round(n / step) * step
}

// Snap to 1/2/5/10 × magnitude. Coarse ON PURPOSE and used only for the fallback, where the
// input is a ceiling that is already round.
// ⚠️ Boundaries are `<`, so exactly 1.5 rounds UP to 2× and exactly 7 rounds up to 10×.
export function niceRound(n) {
  if (!(n > 0)) return n
  const mag = 10 ** Math.floor(Math.log10(n))
  const f = n / mag
  return (f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10) * mag
}

// Positional quantile, matching `niceCeiling`'s own `at()` so the two agree about what a
// percentile means on the same array.
const at = (nums, p) => nums[Math.min(nums.length - 1, Math.floor(nums.length * p))]

/**
 * Buckets for the quick-select chip row.
 * @param {number} ceiling — the axis top, already narrowed by deal type and property type.
 * @param {number[]|null} values — the loaded values for this segment. Given them, the cuts come
 *   from the DATA; without them (or with too few), from the axis. Callers already hold this
 *   array — it is what draws the histogram — so this costs no new plumbing.
 * @returns {{min: number|null, max: number|null}[]} — `null` is an OPEN bound, never zero: the
 *   first bucket has no floor and the last no roof, so the row covers the whole line. Empty when
 *   the range is too narrow to bucket honestly.
 */
// Round to N SIGNIFICANT figures, for histogram bin edges. Bin edges are arbitrary division
// points and the raw values carry full float precision, so a tooltip renders 26315.789… as
// "$26.315789473684212K" without this.
//
// 🚨 DO NOT go back to `Math.round(v / m) * m` with `m = 10 ** (exp - 2)`. That is what this
// replaces, and the FINAL MULTIPLY reintroduces exactly the binary-float error the rounding just
// removed: for v = 41.3 the multiplier is 0.1, `413 * 0.1 === 41.300000000000004`, and the area
// histogram's tooltip shipped reading
//     "41.3000000000000004მ² – 48.400000000000006მ² · 1,411 ლიდი"
// (owner screenshot, 2026-08-26). It was only visible on values whose 3-sig-fig form needs a
// DECIMAL — integers like 26300 multiply back exactly, which is why review never caught it.
// `toPrecision` formats in decimal and never multiplies back, so there is no residue to strip.
export function sigFigs(v, digits = 3) {
    if (!(v > 0) || !Number.isFinite(v)) return 0
    return Number(v.toPrecision(digits))
}

export function rangePresets(ceiling, values = null) {
  const clean = realistic(values)
  let cuts = clean.length >= MIN_SAMPLE
    ? tidy([at(clean, 1 / 3), at(clean, 2 / 3)].map(niceCut), ceiling)
    : []
  // ── 🚨 NO DATA MEANS NO CHIPS. THE AXIS FALLBACK IS NOT A DEFAULT ────────────────────────
  // `values === null` and `values = []` mean two different things and must not share an answer:
  //   · **null** — the caller has data but has WITHDRAWN it, because the current sort makes the
  //     loaded page a biased sample of this very quantity (see presetValues in IntakeTab). The
  //     axis is the sort-independent answer and is the right fallback.
  //   · **an array that is too thin** — nothing has been fetched for THIS query yet, or the pool
  //     genuinely holds almost nothing. Falling back to the axis here prints confident buckets
  //     that describe no search anybody ran.
  // That second case is not hypothetical: the leads cache is keyed per query, so for a beat after
  // every filter change the rows are legitimately absent — and the owner watched the chips flip
  // back to the generic `< $200K · $200K–$500K · $500K+` they had just reported (2026-08-19).
  // Same rule the feed's own counters already follow: `leadsCachedTotal` returns null and renders
  // NOTHING rather than a total from a different filter set. The inputs and the histogram remain,
  // and neither of them pretends.
  if (cuts.length < 2 && values == null) cuts = tidy([0.25, 0.5, 0.75].map(p => niceRound(ceiling * p)), ceiling)
  return bucketsFromCuts(cuts)
}

// The chip row for a set of cuts. `null` is an OPEN bound, never zero.
const bucketsFromCuts = (cuts) => {
  if (cuts.length < 2) return []
  const out = [{ min: null, max: cuts[0] }]
  for (let i = 0; i < cuts.length - 1; i++) out.push({ min: cuts[i], max: cuts[i + 1] })
  out.push({ min: cuts[cuts.length - 1], max: null })
  return out
}

/**
 * The same chip row, cut from a corpus HISTOGRAM instead of the loaded values (0687).
 *
 * The note at `values` above states the invariant this exists to keep: the chips and the bars
 * must describe ONE distribution. Once `search_meta.range_facets` started drawing the bars from
 * the whole search rather than the loaded page, chips cut from the loaded page began describing
 * a different population — measured on the Leads pool, bars showing the mass under $1K beside
 * chips reading "$130K-$190K". Both were correct about different pools, which is worse than
 * either being wrong.
 *
 * Same terciles, same niceCut, same tidy, same open bounds — only the quantile SOURCE differs.
 * @param {number} ceiling — the axis top, from the same facet as the buckets.
 * @param {number[]} buckets — 20 counts: 19 even bins across 0..ceiling, then a ">= ceiling" bin.
 */
export function rangePresetsFromHistogram(ceiling, buckets, floor = null) {
  if (!Array.isArray(buckets) || buckets.length < 2 || !(ceiling > 0)) return []
  const counts = buckets.map(n => (Number.isFinite(+n) ? +n : 0))
  const total = counts.reduce((a, b) => a + b, 0)
  // Same honesty rule as the values path: too small a pool gets no chips rather than confident
  // buckets describing nothing.
  if (total < MIN_SAMPLE) return []
  const bins = counts.length - 1
  const width = ceiling / bins
  // 0697: a `floor` means the bins are LOG-spaced (see the search RPC's rf_bkt), so a bin's
  // representative value is the GEOMETRIC mean of its edges, not the arithmetic one. Using the
  // arithmetic midpoint on a log axis pulls every cut toward the top of its bin — on the leads
  // price axis the first bin spans $200-$282 and its arithmetic middle, $241, is not where its
  // mass sits. No floor => linear, exactly as before.
  const lf = Number(floor)
  const logMode = Number.isFinite(lf) && lf > 0 && lf < ceiling
  const lnF = logMode ? Math.log(lf) : 0
  const lnSpan = logMode ? Math.log(ceiling) - lnF : 0
  const edgeAt = i => (logMode ? Math.exp(lnF + (i / bins) * lnSpan) : i * width)
  const midOf = i => (logMode ? Math.sqrt(edgeAt(i) * edgeAt(i + 1)) : (i + 0.5) * width)
  // Positional quantile over the CDF. A bin is represented by its MIDPOINT — the bars carry no
  // finer detail than that, and pretending otherwise would invent precision the payload lacks.
  const quantile = (pp) => {
    const want = total * pp
    let acc = 0
    for (let i = 0; i < counts.length; i++) {
      acc += counts[i]
      if (acc >= want) return i >= bins ? ceiling : midOf(i)
    }
    return ceiling
  }
  // `tidy` drops a cut that landed on the axis end, so an overflow-heavy facet simply yields
  // fewer chips rather than a bucket with no width.
  return bucketsFromCuts(tidy([quantile(1 / 3), quantile(2 / 3)].map(niceCut), ceiling))
}

// ⚠️ `v >= 1` IS A REAL GUARD, not defensive noise — the first test written against this caught
// it. The snapping is scale-free, so a ceiling of 1 or 2 produces cuts at 0.2 and 0.5 and the row
// renders "< 0.2მ²". Both units here are whole, so a sub-unit boundary is never a real answer.
// `v < ceiling` retires a cut that landed on the axis end — that is where the line stops, not a
// boundary within it — and NaN falls out of the same comparison chain.
const tidy = (raw, ceiling) => [...new Set(raw)].filter(v => v >= 1 && v < ceiling).sort((a, b) => a - b)
